import Cookies from "js-cookie";
import { DateFormats } from "@utils/date";
import dateFormat from "date-fns/format";
import { ROUND_TO_1_DECIMAL } from "@renewals/util";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const getDateSuffix = (d: string) => {
  const longFormatDate = new Date(d);
  const date = longFormatDate.getDate();
  let day: string;
  switch (date) {
    case 1:
      day = `${date.toString()}st`;
      break;
    case 2:
      day = `${date.toString()}nd`;
      break;
    case 3:
      day = `${date.toString()}rd`;
      break;
    case 31:
      day = `${date.toString()}st`;
      break;
    default:
      day = `${date.toString()}th`;
  }
  return day;
};

const getDateFormat = (d: string) => {
  const longFormatDate = new Date(d);
  const day: string = getDateSuffix(d);
  const month = longFormatDate.toString().split(" ")[1];
  const year = longFormatDate
    .getFullYear()
    .toString()
    .substring(2);
  return `${day} ${month} '${year}`;
};

export const getFullMonthDateFormat = (d: string) => {
  const date = new Date(d);
  const day: string = getDateSuffix(d);
  const month: string = monthNames[date.getMonth()];
  const year: number = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const numberOfDaysLeft = (d: string) => {
  const longFormatDate = new Date(d);
  const one_day = 1000 * 60 * 60 * 24;
  const today = new Date();
  const diffDate = (
    Math.round(
      longFormatDate.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)
    ) / one_day
  ).toFixed();
  if (diffDate.length === 1) {
    return "0".concat(diffDate);
  }
  return diffDate;
};

export const numberToCurrency = num => {
  const Intl = window.Intl || null;
  const number = parseInt(num, 10);
  let n;
  const options = {
    maximumFractionDigits: 0
  };
  if (number) {
    if (!Intl) {
      const numberFormatted = number.toString();
      let lastThree = numberFormatted.substring(numberFormatted.length - 3);
      const otherNumbers = numberFormatted.substring(
        0,
        numberFormatted.length - 3
      );
      if (otherNumbers !== "") {
        lastThree = `,${lastThree}`;
      }
      const res =
        otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
      return `₹ ${res}`;
    }
    if (number > 10000000) {
      n = `${(number / 10000000).toFixed(2)} Crores`;
    } else if (number > 100000) {
      n = `${(number / 100000).toFixed(2)} Lakhs`;
    } else {
      const numberFormat = new Intl.NumberFormat("en-IN", options);
      n = numberFormat.format(number);
    }
    return `₹${n}`;
  }
  return null;
};

// Return values:
// - a number < 0 if a < b
// - a number > 0 if a > b
// - 0 if a = b
export const cmpVersions = (a, b) => {
  try {
    let i;
    let diff;
    const regExStrip0 = /(\.0+)+$/;
    const segmentsA = a.replace(regExStrip0, "").split(".");
    const segmentsB = b.replace(regExStrip0, "").split(".");
    const l = Math.min(segmentsA.length, segmentsB.length);

    for (i = 0; i < l; i += 1) {
      diff = parseInt(segmentsA[i], 10) - parseInt(segmentsB[i], 10);
      if (diff) {
        return diff;
      }
    }
    return segmentsA.length - segmentsB.length;
  } catch {
    return 0;
  }
};

export const getPlatform = mweb => {
  if (Cookies.get("webview")) {
    return Cookies.get("webview") === "android_app" ? "android" : "ios";
  }
  return mweb ? "mweb" : "web";
};

export const androidPaymentServiceVersion = "3.4.5";
export const iosPaymentServiceVersion = "1.0.1";

export const tentativePurchaseTextToDays = selectedChoice => {
  switch (selectedChoice) {
    case "Today or tomorrow":
      return 2;
    case "In the next 1 week":
      return 7;
    case "In the next 2 weeks":
      return 14;
    case "I am not sure yet":
      return 10;
    default:
      return 10;
  }
};
export const compressPlanName = (plan: any) => {
  let planName = plan;

  if (planName) {
    planName = planName?.replace(/comprehensive/g, "c");
    planName = planName?.replace(/zero_dep/g, "zd");
    planName = planName?.replace(/deductible/g, "d");
  }

  return planName;
};

export const primaryBlue = "#5E27C9";
export const isBrowser = !!(typeof window !== "undefined");

export const getValidPolicyStartDate = (policyStartDate: any) => {
  let policyStartDate1;
  try {
    policyStartDate1 = `${dateFormat(
      new Date(policyStartDate).getTime(),
      DateFormats.ISO_DATE
    )}T05:30:00+05:30`;
  } catch {
    policyStartDate1 = "";
  }
  return policyStartDate1;
};

export const convertAmountToAmountWithUnits = (
  money: number,
  conversion: string = "floor"
) => {
  const formatter = new Intl.NumberFormat("en-IN");

  const roundMoney = (money: number, method: string) => {
    if (method === ROUND_TO_1_DECIMAL) {
      return parseFloat(money?.toFixed(1));
    }
    return Math[method](money);
  };

  if (money >= 10000000) {
    const crores = roundMoney(money / 10000000, conversion);
    return formatter.format(crores) + " Crores";
  } else if (money >= 100000) {
    const lakhs = roundMoney(money / 100000, conversion);
    return formatter.format(lakhs) + " Lakh";
  } else if (money >= 1000) {
    const thousands = roundMoney(money / 1000, conversion);
    return formatter.format(thousands) + "K";
  } else {
    return formatter.format(money);
  }
};

export const dispatchNREvents = false;

export const isJourneyFlow = path => {
  return (
    path.includes("/car-journey") ||
    path.includes("/bike-journey") ||
    path.includes("/renew-policy") ||
    path.includes("/falcon") ||
    path.includes("/inspection") ||
    path.includes("/new-car")
  );
};

export const isChatbotEnabledJourneys = path => {
  return path.includes("/car-journey");
};

export const FAMILY_ADDONS_IDS = [
  "pa_owner_car",
  "pa_unnamed_passenger_car",
  "legal_liability_car"
];

export const sortCouponsInDescendingOrderOfAmount = coupons => {
  // reverse sorting on basis of coupon_value of coupons
  coupons?.sort((a, b) => b?.coupon_value - a?.coupon_value);
};

export const scrollIntoViewWrapper = (element, blockType = "center") => {
  const target = document.getElementById(element);
  target &&
    target?.scrollIntoView({
      behavior: "smooth",
      //@ts-ignore
      block: blockType,
      inline: "center"
    });
};

export default getDateFormat;
